import { useEffect, useState } from "react";
import { fetchArticles } from "@/lib/strapi";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 6,
  pageCount: 1,
  total: 0,
};

const getArticleKey = (article) => article?.slug || article?.id;

const getUniqueArticles = (currentArticles, incomingArticles) => {
  const seenArticleIds = new Set(currentArticles.map(getArticleKey));
  return incomingArticles.filter((article) => {
    const key = getArticleKey(article);
    if (!key || seenArticleIds.has(key)) {
      return false;
    }

    seenArticleIds.add(key);
    return true;
  });
};

const mergeArticles = (currentArticles, incomingArticles) => {
  return [...currentArticles, ...getUniqueArticles(currentArticles, incomingArticles)];
};

const getResultItems = (result) => {
  return Array.isArray(result) ? result : result?.items || [];
};

export function useArticles(limit = 6) {
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: limit });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadArticles = async () => {
      try {
        setIsLoading(true);
        setError("");
        const result = await fetchArticles({ page: 1, pageSize: limit });
        if (mounted) {
          const nextArticles = getResultItems(result);
          const nextPagination = result?.pagination || { ...DEFAULT_PAGINATION, pageSize: limit };

          setArticles(nextArticles);
          setPagination(nextPagination);
          setHasMore(nextArticles.length > 0);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to load articles");
          setArticles([]);
          setPagination({ ...DEFAULT_PAGINATION, pageSize: limit });
          setHasMore(false);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadArticles();

    return () => {
      mounted = false;
    };
  }, [limit]);

  const loadMore = async () => {
    if (isLoading || isLoadingMore || !hasMore) {
      return;
    }

    try {
      setIsLoadingMore(true);
      setError("");
      const nextPage = pagination.page + 1;
      const result = await fetchArticles({ page: nextPage, pageSize: limit });
      const incomingArticles = getResultItems(result);
      const uniqueIncomingArticles = getUniqueArticles(articles, incomingArticles);
      const resultPagination = result?.pagination || pagination;
      const nextPagination = {
        ...resultPagination,
        page: Math.max(Number(resultPagination.page) || nextPage, nextPage),
      };

      setArticles((currentArticles) => mergeArticles(currentArticles, incomingArticles));
      setPagination(nextPagination);
      setHasMore(
        uniqueIncomingArticles.length > 0 &&
          (nextPagination.page < nextPagination.pageCount || incomingArticles.length >= limit)
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load articles");
    } finally {
      setIsLoadingMore(false);
    }
  };

  return {
    articles,
    pagination,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  };
}
